(() => {
  'use strict';

  if (customElements.get('homepage-hero-slider')) {
    return;
  }

  class HomepageHeroSlider extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.isPointerDown = false;
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.handlePreviousClick = this.handlePreviousClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
      this.handleDotsContainerClick = this.handleDotsContainerClick.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
    }

    connectedCallback() {
      if (this.dataset.sliderInitialized === 'true') {
        return;
      }

      this.dataset.sliderInitialized = 'true';

      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.previousButton = this.querySelector('[data-hero-previous]');
      this.nextButton = this.querySelector('[data-hero-next]');
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.dotsContainer = this.querySelector('.homepage-hero-slider__dots');
      this.viewport = this.querySelector('[data-hero-viewport]');

      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';

      this.autoplaySpeed = Math.max(
        parseInt(this.dataset.autoplaySpeed, 10) || 7000,
        3000
      );

      this.transitionSpeed = Math.min(
        Math.max(
          parseInt(this.dataset.transitionSpeed, 10) || 600,
          200
        ),
        1500
      );

      this.style.setProperty(
        '--homepage-hero-transition-speed',
        `${this.transitionSpeed}ms`
      );

      const activeSlideIndex = this.slides.findIndex((slide) =>
        slide.classList.contains('is-active')
      );

      this.currentIndex =
        activeSlideIndex >= 0
          ? activeSlideIndex
          : 0;

      this.updateSlides(this.currentIndex, false);

      if (this.slides.length <= 1) {
        return;
      }

      this.previousButton?.addEventListener(
        'click',
        this.handlePreviousClick
      );

      this.nextButton?.addEventListener(
        'click',
        this.handleNextClick
      );

      this.dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          this.handleDotClick
        );
      });

      this.dotsContainer?.addEventListener(
        'click',
        this.handleDotsContainerClick
      );

      this.addEventListener(
        'keydown',
        this.handleKeydown
      );

      this.addEventListener(
        'focusin',
        this.handleFocusIn
      );

      this.addEventListener(
        'focusout',
        this.handleFocusOut
      );

      if (this.pauseOnHover) {
        this.addEventListener(
          'mouseenter',
          this.handleMouseEnter
        );

        this.addEventListener(
          'mouseleave',
          this.handleMouseLeave
        );
      }

      this.viewport?.addEventListener(
        'pointerdown',
        this.handlePointerDown,
        { passive: true }
      );

      this.viewport?.addEventListener(
        'pointerup',
        this.handlePointerUp,
        { passive: true }
      );

      this.viewport?.addEventListener(
        'pointercancel',
        this.handlePointerUp,
        { passive: true }
      );

      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );

      document.addEventListener(
        'shopify:block:select',
        this.handleBlockSelect
      );

      if (
        typeof this.reduceMotionQuery.addEventListener === 'function'
      ) {
        this.reduceMotionQuery.addEventListener(
          'change',
          this.handleReducedMotionChange
        );
      } else {
        this.reduceMotionQuery.addListener(
          this.handleReducedMotionChange
        );
      }

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();

      this.previousButton?.removeEventListener(
        'click',
        this.handlePreviousClick
      );

      this.nextButton?.removeEventListener(
        'click',
        this.handleNextClick
      );

      this.dots?.forEach((dot) => {
        dot.removeEventListener(
          'click',
          this.handleDotClick
        );
      });

      this.dotsContainer?.removeEventListener(
        'click',
        this.handleDotsContainerClick
      );

      this.removeEventListener(
        'keydown',
        this.handleKeydown
      );

      this.removeEventListener(
        'focusin',
        this.handleFocusIn
      );

      this.removeEventListener(
        'focusout',
        this.handleFocusOut
      );

      this.removeEventListener(
        'mouseenter',
        this.handleMouseEnter
      );

      this.removeEventListener(
        'mouseleave',
        this.handleMouseLeave
      );

      this.viewport?.removeEventListener(
        'pointerdown',
        this.handlePointerDown
      );

      this.viewport?.removeEventListener(
        'pointerup',
        this.handlePointerUp
      );

      this.viewport?.removeEventListener(
        'pointercancel',
        this.handlePointerUp
      );

      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );

      document.removeEventListener(
        'shopify:block:select',
        this.handleBlockSelect
      );

      if (
        typeof this.reduceMotionQuery.removeEventListener === 'function'
      ) {
        this.reduceMotionQuery.removeEventListener(
          'change',
          this.handleReducedMotionChange
        );
      } else {
        this.reduceMotionQuery.removeListener(
          this.handleReducedMotionChange
        );
      }

      delete this.dataset.sliderInitialized;
    }

    handlePreviousClick() {
      this.showPrevious(true);
    }

    handleNextClick() {
      this.showNext(true);
    }

    handleDotClick(event) {
      const index = parseInt(
        event.currentTarget.dataset.heroDot,
        10
      );

      if (Number.isNaN(index)) {
        return;
      }

      /*
       * Clicking an inactive indicator jumps directly
       * to that slide.
       *
       * Clicking the currently-active indicator advances
       * to the next slide.
       */
      if (index === this.currentIndex) {
        this.showNext(true);
        return;
      }

      this.goToSlide(index, true);
    }

    handleDotsContainerClick(event) {
      /*
       * Individual pagination buttons handle their own clicks.
       * This makes the unused grey space inside the pill
       * useful as previous / next navigation.
       */
      if (event.target.closest('[data-hero-dot]')) {
        return;
      }

      const rect =
        this.dotsContainer?.getBoundingClientRect();

      if (
        !rect ||
        rect.width <= 0
      ) {
        return;
      }

      const midpoint =
        rect.left + rect.width / 2;

      if (event.clientX < midpoint) {
        this.showPrevious(true);
      } else {
        this.showNext(true);
      }
    }

    handleMouseEnter() {
      this.stopAutoplay();
    }

    handleMouseLeave() {
      this.startAutoplay();
    }

    handleFocusIn() {
      this.stopAutoplay();
    }

    handleFocusOut() {
      window.requestAnimationFrame(() => {
        if (
          !this.contains(document.activeElement)
        ) {
          this.startAutoplay();
        }
      });
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    handleReducedMotionChange() {
      if (this.reduceMotionQuery.matches) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showPrevious(true);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showNext(true);
      }
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse') {
        return;
      }

      this.isPointerDown = true;

      this.pointerStartX =
        event.clientX;

      this.pointerStartY =
        event.clientY;
    }

    handlePointerUp(event) {
      if (
        !this.isPointerDown ||
        event.pointerType === 'mouse'
      ) {
        return;
      }

      this.isPointerDown = false;

      const distanceX =
        event.clientX - this.pointerStartX;

      const distanceY =
        event.clientY - this.pointerStartY;

      if (
        Math.abs(distanceX) < 50 ||
        Math.abs(distanceX) <= Math.abs(distanceY)
      ) {
        return;
      }

      if (distanceX > 0) {
        this.showPrevious(true);
      } else {
        this.showNext(true);
      }
    }

    handleBlockSelect(event) {
      const selectedSlide =
        event.target.closest?.('[data-hero-slide]');

      if (
        !selectedSlide ||
        !this.contains(selectedSlide)
      ) {
        return;
      }

      const index =
        this.slides.indexOf(selectedSlide);

      if (index >= 0) {
        this.goToSlide(
          index,
          false
        );

        this.stopAutoplay();
      }
    }

    showPrevious(userInitiated = false) {
      const previousIndex =
        (
          this.currentIndex -
          1 +
          this.slides.length
        ) %
        this.slides.length;

      this.goToSlide(
        previousIndex,
        userInitiated
      );
    }

    showNext(userInitiated = false) {
      const nextIndex =
        (
          this.currentIndex +
          1
        ) %
        this.slides.length;

      this.goToSlide(
        nextIndex,
        userInitiated
      );
    }

    goToSlide(index, userInitiated = false) {
      if (
        index === this.currentIndex ||
        index < 0 ||
        index >= this.slides.length
      ) {
        return;
      }

      this.currentIndex = index;

      this.updateSlides(
        index,
        true
      );

      if (userInitiated) {
        this.restartAutoplay();
      }
    }

    updateSlides(activeIndex, announceChange) {
      this.slides.forEach(
        (slide, index) => {
          const isActive =
            index === activeIndex;

          slide.classList.toggle(
            'is-active',
            isActive
          );

          slide.setAttribute(
            'aria-hidden',
            isActive
              ? 'false'
              : 'true'
          );

          slide.inert =
            !isActive;
        }
      );

      this.dots.forEach(
        (dot, index) => {
          const isActive =
            index === activeIndex;

          dot.classList.toggle(
            'is-active',
            isActive
          );

          dot.setAttribute(
            'aria-current',
            isActive
              ? 'true'
              : 'false'
          );
        }
      );

      if (announceChange) {
        const liveRegion =
          this.querySelector(
            '[data-hero-live-region]'
          );

        if (liveRegion) {
          liveRegion.textContent =
            `Slide ${activeIndex + 1} of ${this.slides.length}`;
        }
      }
    }

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        this.slides.length <= 1 ||
        document.hidden ||
        this.reduceMotionQuery.matches ||
        (
          this.matches(':hover') &&
          this.pauseOnHover
        ) ||
        this.contains(document.activeElement)
      ) {
        return;
      }

      this.stopAutoplay();

      this.autoplayTimer =
        window.setInterval(
          () => this.showNext(false),
          this.autoplaySpeed
        );
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(
          this.autoplayTimer
        );

        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define(
    'homepage-hero-slider',
    HomepageHeroSlider
  );
})();